import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../services/api';
import Navbar from '../components/Navbar';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'Unknown'];
const RELATIONSHIPS = ['self', 'spouse', 'parent', 'child', 'sibling', 'other'];
const EMERGENCY_RELATIONS = ['Spouse', 'Parent', 'Child', 'Sibling', 'Friend', 'Other'];
const GENDERS = ['Male', 'Female', 'Other', 'Prefer not to say'];

// Email validation regex
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Phone validation: should start with + and have 10-15 digits
const isValidPhone = (phone) => {
  if (!phone) return true; // Optional field
  return /^\+\d{10,15}$/.test(phone);
};

function PatientForm({ user }) {
  const navigate = useNavigate();
  const { patientId } = useParams();

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    blood_type: '',
    allergies: '',
    phone: '',
    email: '',
    address: '',
    height: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relation: '',
    relationship_to_admin: ''
  });

  const [isEdit, setIsEdit] = useState(!!patientId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [generatedPatientId, setGeneratedPatientId] = useState('');
  const [errors, setErrors] = useState({});

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  const fetchPatient = useCallback(async (pid) => {
    try {
      const response = await api.get(`${API_URL}/patients/${pid}`, { headers });
      const patient = response.data.patient;
      setFormData({
        first_name: patient.first_name || '',
        last_name: patient.last_name || '',
        date_of_birth: patient.date_of_birth || '',
        gender: patient.gender || '',
        blood_type: patient.blood_type || '',
        allergies: patient.allergies || '',
        phone: patient.phone || '',
        email: patient.email || '',
        address: patient.address || '',
        height: patient.height || '',
        emergency_contact_name: patient.emergency_contact_name || '',
        emergency_contact_phone: patient.emergency_contact_phone || '',
        emergency_contact_relation: patient.emergency_contact_relation || '',
        relationship_to_admin: patient.relationship_to_admin || 'self'
      });
      setGeneratedPatientId(patient.patient_id);
      setIsEdit(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load patient data');
    }
  }, [headers]);

  useEffect(() => {
    if (isEdit && patientId) {
      fetchPatient(patientId);
    }
  }, [isEdit, patientId, fetchPatient]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    const newErrors = {};

    // Required fields validation
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!formData.date_of_birth.trim()) {
      newErrors.date_of_birth = 'Date of birth is required';
    }

    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+\d{1,4}\d{10,15}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Enter valid phone with country code (e.g., +919876543210)';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.emergency_contact_name.trim()) {
      newErrors.emergency_contact_name = 'Emergency contact name is required';
    }

    if (!formData.emergency_contact_phone.trim()) {
      newErrors.emergency_contact_phone = 'Emergency contact phone is required';
    } else if (!isValidPhone(formData.emergency_contact_phone)) {
      newErrors.emergency_contact_phone = 'Enter valid phone with country code (e.g., +919876543210)';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setError('Please fix the errors below');
      setLoading(false);
      return;
    }

    try {
      const payload = {
        ...formData,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        address: formData.address.trim(),
        height: formData.height ? parseFloat(formData.height) : null,
        emergency_contact_name: formData.emergency_contact_name.trim(),
        emergency_contact_phone: formData.emergency_contact_phone.trim(),
        emergency_contact_relation: formData.emergency_contact_relation.trim()
      };

      if (isEdit) {
        await api.put(`${API_URL}/patients/${patientId}`, payload, { headers });
        setSuccess('Patient profile updated successfully!');
      } else {
        await api.post(`${API_URL}/patients`, payload, { headers });
        setSuccess('Patient profile created successfully!');
        setTimeout(() => {
          navigate('/dashboard');
        }, 1000);
      }
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${isEdit ? 'update' : 'create'} patient profile`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard">
      <Navbar user={user} onLogout={handleLogout} />

      <div className="dashboard-content">
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2>{isEdit ? 'Edit Patient Profile' : 'Add Patient Profile'}</h2>
          <Link to="/dashboard" className="back-link" style={{ color: '#2b6cb0', textDecoration: 'none' }}>
            ← Back
          </Link>
        </div>

        {error && <div className="api-error">{error}</div>}
        {success && <div className="success-message" style={{ backgroundColor: '#c6f6d5', color: '#22543d', padding: '12px', borderRadius: '8px', marginBottom: '20px' }}>{success}</div>}

        <div className="patient-form-container" style={{ background: 'white', padding: '32px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', maxWidth: '800px', margin: '0 auto' }}>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="first_name" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>First Name *</label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder="Enter first name"
                  style={{ width: '100%', padding: '12px', border: errors.first_name ? '2px solid #e53e3e' : '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem' }}
                  required
                />
                {errors.first_name && <span className="error-message" style={{ color: '#e53e3e', fontSize: '0.875rem', marginTop: '4px', display: 'block' }}>{errors.first_name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="last_name" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Last Name *</label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder="Enter last name"
                  style={{ width: '100%', padding: '12px', border: errors.last_name ? '2px solid #e53e3e' : '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem' }}
                  required
                />
                {errors.last_name && <span className="error-message" style={{ color: '#e53e3e', fontSize: '0.875rem', marginTop: '4px', display: 'block' }}>{errors.last_name}</span>}
              </div>
            </div>

            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '20px' }}>
              {isEdit && (
                <div className="form-group">
                  <label style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Patient ID</label>
                  <input
                    type="text"
                    value={generatedPatientId}
                    readOnly
                    style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem', backgroundColor: '#f7fafc', color: '#718096' }}
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="date_of_birth" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Date of Birth *</label>
                <input
                  type="date"
                  id="date_of_birth"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleChange}
                  style={{ width: '100%', padding: '12px', border: errors.date_of_birth ? '2px solid #e53e3e' : '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem' }}
                  required
                />
                {errors.date_of_birth && <span className="error-message" style={{ color: '#e53e3e', fontSize: '0.875rem', marginTop: '4px', display: 'block' }}>{errors.date_of_birth}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="gender" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Gender *</label>
                <select
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  style={{ width: '100%', padding: '12px', border: errors.gender ? '2px solid #e53e3e' : '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem' }}
                  required
                >
                  <option value="">Select gender</option>
                  {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
                {errors.gender && <span className="error-message" style={{ color: '#e53e3e', fontSize: '0.875rem', marginTop: '4px', display: 'block' }}>{errors.gender}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="blood_type" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Blood Type</label>
                <select
                  id="blood_type"
                  name="blood_type"
                  value={formData.blood_type}
                  onChange={handleChange}
                  style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem' }}
                >
                  <option value="">Select blood type</option>
                  {BLOOD_TYPES.map(bt => <option key={bt} value={bt}>{bt}</option>)}
                </select>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label htmlFor="allergies" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Allergies</label>
              <textarea
                id="allergies"
                name="allergies"
                value={formData.allergies}
                onChange={handleChange}
                placeholder="List any allergies (comma-separated)"
                rows="2"
                style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem', resize: 'vertical' }}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91XXXXXXXXXX"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: errors.phone ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
                {errors.phone && <span className="error-message" style={{ color: '#e53e3e', fontSize: '0.875rem', marginTop: '4px', display: 'block' }}>{errors.phone}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="email" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: errors.email ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
                {errors.email && <span className="error-message" style={{ color: '#e53e3e', fontSize: '0.875rem', marginTop: '4px', display: 'block' }}>{errors.email}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="relationship_to_admin" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>
                  Relationship
                </label>
                <select
                  id="relationship_to_admin"
                  name="relationship_to_admin"
                  value={formData.relationship_to_admin}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">Select relationship</option>
                  {RELATIONSHIPS.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                </select>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label htmlFor="address" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Address</label>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Enter full address"
                rows="3"
                style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem', resize: 'vertical' }}
              />
            </div>

            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px', marginBottom: '20px' }}>
              <div className="form-group">
                <label htmlFor="height" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>
                  Height (cm, optional)
                </label>
                <input
                  type="number"
                  id="height"
                  name="height"
                  value={formData.height}
                  onChange={handleChange}
                  placeholder="e.g., 175"
                  step="0.1"
                  style={{ width: '100%', maxWidth: '300px', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem' }}
                />
                <small style={{ color: '#718096', fontSize: '0.875rem', marginTop: '4px', display: 'block' }}>
                  Used for automatic BMI calculation when recording weight vitals
                </small>
              </div>
            </div>

            <div className="emergency-section" style={{ backgroundColor: '#f7fafc', padding: '24px', borderRadius: '12px', marginBottom: '24px' }}>
              <h4 style={{ color: '#2d3748', marginBottom: '16px' }}>Emergency Contact</h4>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="emergency_contact_name" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Contact Name *</label>
                  <input
                    type="text"
                    id="emergency_contact_name"
                    name="emergency_contact_name"
                    value={formData.emergency_contact_name}
                    onChange={handleChange}
                    placeholder="Enter emergency contact name"
                    style={{ width: '100%', padding: '12px', border: errors.emergency_contact_name ? '2px solid #e53e3e' : '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem' }}
                  />
                  {errors.emergency_contact_name && <span className="error-message" style={{ color: '#e53e3e', fontSize: '0.875rem', marginTop: '4px', display: 'block' }}>{errors.emergency_contact_name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="emergency_contact_phone" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Contact Phone *</label>
                  <input
                    type="tel"
                    id="emergency_contact_phone"
                    name="emergency_contact_phone"
                    value={formData.emergency_contact_phone}
                    onChange={handleChange}
                    placeholder="Enter emergency contact phone (e.g., +919876543210)"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: errors.emergency_contact_phone ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      boxSizing: 'border-box'
                    }}
                  />
                  {errors.emergency_contact_phone && <span className="error-message" style={{ color: '#e53e3e', fontSize: '0.875rem', marginTop: '4px', display: 'block' }}>{errors.emergency_contact_phone}</span>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="emergency_contact_relation" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Relationship</label>
                <select
                  id="emergency_contact_relation"
                  name="emergency_contact_relation"
                  value={formData.emergency_contact_relation}
                  onChange={handleChange}
                  style={{ width: '100%', maxWidth: '300px', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem' }}
                >
                  <option value="">Select relationship</option>
                  {EMERGENCY_RELATIONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>

            <div className="form-actions" style={{ display: 'flex', gap: '12px', marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e2e8f0' }}>
              <button
                type="submit"
                className="submit-btn"
                disabled={loading}
                style={{ padding: '12px 32px' }}
              >
                {loading ? 'Saving...' : (isEdit ? 'Update Profile' : 'Create Profile')}
              </button>
              <Link
                to="/dashboard"
                style={{
                  padding: '12px 32px',
                  backgroundColor: '#cbd5e0',
                  color: '#4a5568',
                  textDecoration: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default PatientForm;
